<template>
    <div class="min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminNavbar active="coupons" />

        <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
                <div class="min-w-0">
                    <router-link :to="{ name: 'adminCoupons', params: { uuid } }" class="text-gray-500 hover:text-gray-800 text-sm">← Cupons</router-link>
                    <h1 class="text-base sm:text-xl font-bold text-gray-800 mt-2">
                        {{ isCreateMode ? 'Novo cupom' : (coupon?.name || 'Detalhes do cupom') }}
                    </h1>
                    <p class="text-sm text-gray-400 mt-1">
                        {{ isCreateMode ? 'Crie cupom e promotion code inicial no Stripe.' : 'Gerencie nome e promotion codes deste cupom.' }}
                    </p>
                </div>
                <div v-if="!isCreateMode && coupon" class="flex flex-wrap gap-2">
                    <a :href="buildStripeCouponUrl(coupon.id)" target="_blank" rel="noopener noreferrer" class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight">
                        Ver no Stripe
                    </a>
                    <button type="button" class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight !bg-red-600 hover:!bg-red-700 !text-white !border-red-600" :disabled="deletingCoupon" @click="handleDeleteCoupon">
                        {{ deletingCoupon ? 'Apagando...' : 'Apagar cupom' }}
                    </button>
                </div>
            </div>

            <div v-if="loadError" class="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {{ loadError }}
            </div>

            <div v-if="isLoading" class="space-y-3">
                <div class="skeleton h-40 w-full rounded-xl"></div>
                <div class="skeleton h-48 w-full rounded-xl"></div>
            </div>

            <div v-else class="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div class="space-y-6">
                    <template v-if="isCreateMode">
                        <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
                            <h2 class="text-sm font-semibold text-gray-500 mb-4">Cupom do Stripe</h2>
                            <div class="grid gap-4 md:grid-cols-2">
                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Nome do cupom
                                    <input v-model="form.couponName" type="text" class="input input-bordered text-sm" placeholder="Desconto 30" />
                                </label>

                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Tipo de desconto
                                    <select v-model="form.discountType" class="select select-bordered text-sm">
                                        <option value="percentage">Porcentagem</option>
                                        <option value="fixed">Valor fixo</option>
                                    </select>
                                </label>

                                <label v-if="form.discountType === 'percentage'" class="flex flex-col gap-1 text-xs text-gray-500">
                                    Percentual de desconto
                                    <input v-model="form.discountValue" type="number" min="1" max="100" class="input input-bordered text-sm" placeholder="30" />
                                </label>

                                <label v-else class="flex flex-col gap-1 text-xs text-gray-500">
                                    Valor do desconto em BRL
                                    <input v-model="form.discountValue" type="number" min="0" step="0.01" class="input input-bordered text-sm" placeholder="50.00" />
                                </label>

                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Duração
                                    <select v-model="form.duration" class="select select-bordered text-sm">
                                        <option value="once">Uma vez</option>
                                        <option value="repeating">Vários meses</option>
                                        <option value="forever">Para sempre</option>
                                    </select>
                                </label>

                                <label v-if="form.duration === 'repeating'" class="flex flex-col gap-1 text-xs text-gray-500">
                                    Quantidade de meses
                                    <input v-model="form.durationInMonths" type="number" min="1" class="input input-bordered text-sm" placeholder="3" />
                                </label>

                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Data limite para uso
                                    <input v-model="form.redeemBy" type="date" class="input input-bordered text-sm" />
                                </label>

                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Máximo de usos total
                                    <input v-model="form.maxRedemptions" type="number" min="1" class="input input-bordered text-sm" placeholder="100" />
                                </label>
                            </div>
                        </div>

                        <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
                            <h2 class="text-sm font-semibold text-gray-500 mb-4">Promotion code inicial</h2>
                            <div class="grid gap-4 md:grid-cols-2">
                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Código promocional
                                    <input v-model="form.code" type="text" class="input input-bordered text-sm font-mono" placeholder="PROMO30" />
                                </label>

                                <label class="flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-3 text-sm text-gray-600">
                                    <input v-model="form.onlyNewCustomers" type="checkbox" class="checkbox checkbox-sm" />
                                    Apenas para novos clientes
                                </label>
                            </div>
                        </div>

                        <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
                            <button type="button" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" :disabled="isSaving" @click="handleSaveCoupon">
                                {{ isSaving ? 'Salvando...' : 'Salvar cupom' }}
                            </button>
                        </div>
                    </template>

                    <template v-else-if="coupon">
                        <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5">
                            <div class="flex flex-wrap items-center gap-2 mb-4">
                                <span :class="adminBadgeClass(coupon.valid ? 'success' : 'error')">{{ coupon.valid ? 'Ativo' : 'Inativo' }}</span>
                                <span class="text-xs font-mono text-gray-400 break-all">{{ coupon.id }}</span>
                            </div>

                            <div class="grid gap-4 md:grid-cols-2">
                                <div class="md:col-span-2">
                                    <div class="text-xs uppercase tracking-wide text-gray-400 mb-1">Nome do cupom</div>
                                    <div class="flex flex-col gap-2 sm:flex-row">
                                        <input v-model="couponNameDraft" type="text" class="input input-bordered text-sm flex-1 min-w-0" placeholder="Nome do cupom" />
                                        <button type="button" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight whitespace-nowrap" :disabled="isUpdatingCouponName" @click="handleUpdateCouponName">
                                            {{ isUpdatingCouponName ? 'Salvando...' : 'Salvar nome' }}
                                        </button>
                                    </div>
                                </div>

                                <div class="rounded-lg bg-gray-50 px-4 py-3">
                                    <div class="text-xs uppercase tracking-wide text-gray-400">Desconto</div>
                                    <div class="text-sm font-semibold text-gray-700 mt-1">{{ formatDiscount(coupon) }}</div>
                                </div>
                                <div class="rounded-lg bg-gray-50 px-4 py-3">
                                    <div class="text-xs uppercase tracking-wide text-gray-400">Duração</div>
                                    <div class="text-sm font-semibold text-gray-700 mt-1">{{ formatDuration(coupon) }}</div>
                                </div>
                                <div class="rounded-lg bg-gray-50 px-4 py-3">
                                    <div class="text-xs uppercase tracking-wide text-gray-400">Validade</div>
                                    <div class="text-sm font-semibold text-gray-700 mt-1">{{ formatValidity(coupon.redeem_by) }}</div>
                                </div>
                                <div class="rounded-lg bg-gray-50 px-4 py-3">
                                    <div class="text-xs uppercase tracking-wide text-gray-400">Usos</div>
                                    <div class="text-sm font-semibold text-gray-700 mt-1">{{ coupon.times_redeemed }}<span v-if="coupon.max_redemptions"> / {{ coupon.max_redemptions }}</span></div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="bg-white border border-gray-100 shadow-sm rounded-xl p-5 self-start">
                    <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                        <div>
                            <h2 class="text-sm font-semibold text-gray-500">Promotion codes</h2>
                            <p class="text-xs text-gray-400 mt-1">
                                {{ isCreateMode ? 'Salve o cupom primeiro para criar códigos.' : 'Crie ou desative códigos promocionais deste cupom.' }}
                            </p>
                        </div>
                        <button v-if="!isCreateMode && coupon" type="button" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight self-start sm:self-auto" @click="showPromotionCodeForm = !showPromotionCodeForm">
                            {{ showPromotionCodeForm ? 'Cancelar' : 'Adicionar código' }}
                        </button>
                    </div>

                    <div v-if="isCreateMode" class="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                        Após salvar o cupom, os códigos adicionais ficam disponíveis aqui.
                    </div>

                    <template v-else>
                        <div v-if="showPromotionCodeForm" class="rounded-lg border border-gray-100 p-4 mb-4">
                            <div class="grid gap-3">
                                <label class="flex flex-col gap-1 text-xs text-gray-500">
                                    Código promocional
                                    <input v-model="promotionCodeForm.code" type="text" class="input input-bordered text-sm font-mono" placeholder="PROMO30VIP" />
                                </label>
                                <label class="flex items-center gap-2 rounded-lg border border-gray-100 px-3 py-3 text-sm text-gray-600">
                                    <input v-model="promotionCodeForm.onlyNewCustomers" type="checkbox" class="checkbox checkbox-sm" />
                                    Apenas para novos clientes
                                </label>
                                <div>
                                    <button type="button" class="btn btn-xs btn-primary text-white !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" :disabled="isAddingPromotionCode" @click="handleAddPromotionCode">
                                        {{ isAddingPromotionCode ? 'Salvando...' : 'Salvar código' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div v-if="promotionCodeItems.length === 0" class="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                            Nenhum promotion code associado ainda.
                        </div>

                        <div v-else class="space-y-3">
                            <div v-for="item in promotionCodeItems" :key="item.id" class="rounded-lg border border-gray-100 p-4">
                                <div class="flex flex-wrap items-center gap-2 mb-2">
                                    <span :class="adminBadgeClass(item.active ? 'success' : 'neutral')">{{ item.active ? 'Ativo' : 'Inativo' }}</span>
                                    <span v-if="item.only_new_customers" :class="adminBadgeClass('info')">Novos clientes</span>
                                </div>
                                <div class="text-sm font-semibold font-mono text-gray-800 break-all">{{ item.code }}</div>
                                <div class="grid grid-cols-2 gap-3 mt-3 text-sm">
                                    <div>
                                        <div class="text-[11px] uppercase tracking-wide text-gray-400">Validade</div>
                                        <div class="text-gray-600 mt-1">{{ formatValidity(item.expires_at) }}</div>
                                    </div>
                                    <div>
                                        <div class="text-[11px] uppercase tracking-wide text-gray-400">Usos</div>
                                        <div class="text-gray-600 mt-1">{{ item.times_redeemed }}<span v-if="item.max_redemptions"> / {{ item.max_redemptions }}</span></div>
                                    </div>
                                </div>
                                <div class="pt-3 mt-3 border-t border-gray-100">
                                    <button type="button" class="text-xs text-red-500 hover:text-red-700 transition-colors" :disabled="!item.active || deletingPromotionCodeId === item.id" @click="handleDeletePromotionCode(item)">
                                        {{ deletingPromotionCodeId === item.id ? 'Desativando...' : 'Desativar código' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import AdminNavbar from '@/components/admin/AdminNavbar.vue';
import { adminBadgeClass } from '@/constants/adminBadge';
import AdminStripeService from '@/services/admin/AdminStripeService';

const STRIPE_COUPONS_URL = 'https://dashboard.stripe.com/coupons';

function defaultForm() {
    return {
        couponName: '',
        code: '',
        discountType: 'percentage',
        discountValue: '',
        duration: 'once',
        durationInMonths: '',
        redeemBy: '',
        maxRedemptions: '',
        onlyNewCustomers: false
    };
}

export default {
    name: 'AdminCouponDetail',
    components: { AdminNavbar },
    setup() {
        const toast = useToast();
        const route = useRoute();
        const router = useRouter();
        const uuid = computed(() => route.params.uuid);
        const couponId = computed(() => route.params.couponId);
        const isCreateMode = computed(() => route.name === 'adminCouponNew');

        const coupon = ref(null);
        const couponNameDraft = ref('');
        const promotionCodeItems = ref([]);
        const isLoading = ref(false);
        const loadError = ref('');
        const isSaving = ref(false);
        const isUpdatingCouponName = ref(false);
        const isAddingPromotionCode = ref(false);
        const deletingPromotionCodeId = ref('');
        const deletingCoupon = ref(false);
        const showPromotionCodeForm = ref(false);

        const form = reactive(defaultForm());
        const promotionCodeForm = reactive({
            code: '',
            onlyNewCustomers: false
        });

        function resetForm() {
            Object.assign(form, defaultForm());
        }

        function resetDetailState() {
            coupon.value = null;
            couponNameDraft.value = '';
            promotionCodeItems.value = [];
            showPromotionCodeForm.value = false;
            promotionCodeForm.code = '';
            promotionCodeForm.onlyNewCustomers = false;
        }

        function applyCouponData(data) {
            coupon.value = data;
            couponNameDraft.value = data?.name || '';
            promotionCodeItems.value = data?.promotion_codes || [];
        }

        async function loadCouponDetail() {
            if (isCreateMode.value || !couponId.value) {
                loadError.value = '';
                isLoading.value = false;
                resetDetailState();
                resetForm();
                return;
            }

            isLoading.value = true;
            loadError.value = '';
            try {
                const response = await AdminStripeService.getCoupon(couponId.value);
                applyCouponData(response?.data?.data || null);
            } catch (err) {
                loadError.value = err.response?.data?.text || 'Erro ao carregar cupom';
                resetDetailState();
            } finally {
                isLoading.value = false;
            }
        }

        async function handleSaveCoupon() {
            if (isSaving.value) return;

            isSaving.value = true;
            try {
                const response = await AdminStripeService.createCoupon({
                    coupon_name: form.couponName,
                    code: form.code,
                    discount_type: form.discountType,
                    discount_value: form.discountValue,
                    duration_type: form.duration,
                    duration_in_months: form.duration === 'repeating' ? form.durationInMonths : null,
                    redeem_by: form.redeemBy || null,
                    max_redemptions: form.maxRedemptions || null,
                    only_new_customers: form.onlyNewCustomers
                });

                const data = response?.data?.data || null;
                if (data && data.id) {
                    applyCouponData(data);
                    toast.open({ message: 'Cupom criado com sucesso no Stripe.', type: 'success', position: 'top-right' });
                    router.replace({ name: 'adminCouponDetail', params: { uuid: uuid.value, couponId: data.id } });
                }
            } catch (err) {
                toast.open({
                    message: err.response?.data?.text || 'Não foi possível criar o cupom.',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                isSaving.value = false;
            }
        }

        async function handleUpdateCouponName() {
            if (!coupon.value?.id || isUpdatingCouponName.value) return;

            isUpdatingCouponName.value = true;
            try {
                const response = await AdminStripeService.updateCouponName(coupon.value.id, {
                    coupon_name: couponNameDraft.value
                });
                coupon.value = {
                    ...coupon.value,
                    ...(response?.data?.data || {})
                };
                couponNameDraft.value = coupon.value.name || couponNameDraft.value;
                toast.open({ message: 'Nome do cupom atualizado.', type: 'success', position: 'top-right' });
            } catch (err) {
                toast.open({
                    message: err.response?.data?.text || 'Não foi possível atualizar o nome do cupom.',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                isUpdatingCouponName.value = false;
            }
        }

        async function handleAddPromotionCode() {
            if (!coupon.value?.id || isAddingPromotionCode.value) return;

            isAddingPromotionCode.value = true;
            try {
                const response = await AdminStripeService.createPromotionCode(coupon.value.id, {
                    code: promotionCodeForm.code,
                    only_new_customers: promotionCodeForm.onlyNewCustomers
                });
                const created = response?.data?.data || null;
                if (created) {
                    promotionCodeItems.value = [created, ...promotionCodeItems.value];
                }
                promotionCodeForm.code = '';
                promotionCodeForm.onlyNewCustomers = false;
                showPromotionCodeForm.value = false;
                toast.open({ message: 'Promotion code criado com sucesso.', type: 'success', position: 'top-right' });
            } catch (err) {
                toast.open({
                    message: err.response?.data?.text || 'Não foi possível criar o promotion code.',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                isAddingPromotionCode.value = false;
            }
        }

        async function handleDeletePromotionCode(item) {
            if (!coupon.value?.id || !item?.id || deletingPromotionCodeId.value) return;

            const confirmed = window.confirm(`Desativar código ${item.code}?`);
            if (!confirmed) return;

            deletingPromotionCodeId.value = item.id;
            try {
                await AdminStripeService.deactivatePromotionCode(coupon.value.id, item.id);
                promotionCodeItems.value = promotionCodeItems.value.map(current => current.id === item.id ? { ...current, active: false } : current);
                toast.open({ message: 'Promotion code desativado.', type: 'success', position: 'top-right' });
            } catch (err) {
                toast.open({
                    message: err.response?.data?.text || 'Não foi possível desativar o promotion code.',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                deletingPromotionCodeId.value = '';
            }
        }

        async function handleDeleteCoupon() {
            if (!coupon.value?.id || deletingCoupon.value) return;

            const confirmed = window.confirm(`Apagar cupom ${coupon.value.name}? Isso desativa os promotion codes dele no Stripe.`);
            if (!confirmed) return;

            deletingCoupon.value = true;
            try {
                await AdminStripeService.deleteCoupon(coupon.value.id);
                toast.open({ message: 'Cupom apagado com sucesso.', type: 'success', position: 'top-right' });
                router.push({ name: 'adminCoupons', params: { uuid: uuid.value } });
            } catch (err) {
                toast.open({
                    message: err.response?.data?.text || 'Não foi possível apagar o cupom.',
                    type: 'error',
                    position: 'top-right'
                });
            } finally {
                deletingCoupon.value = false;
            }
        }

        function buildStripeCouponUrl(id) {
            return `${STRIPE_COUPONS_URL}/${id}`;
        }

        function formatDiscount(item) {
            if (item?.percent_off) return `${item.percent_off}% off`;
            if (item?.amount_off) return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.amount_off);
            return '—';
        }

        function formatDuration(item) {
            if (item?.duration === 'once') return 'Uma vez';
            if (item?.duration === 'repeating') return item.duration_in_months ? `${item.duration_in_months} mês(es)` : 'Vários meses';
            if (item?.duration === 'forever') return 'Para sempre';
            return '—';
        }

        function formatValidity(value) {
            if (!value) return 'Sem limite';
            return new Date(value).toLocaleDateString('pt-BR');
        }

        watch(() => route.fullPath, () => {
            loadCouponDetail();
        });

        onMounted(() => {
            loadCouponDetail();
        });

        return {
            uuid,
            isCreateMode,
            coupon,
            couponNameDraft,
            promotionCodeItems,
            isLoading,
            loadError,
            isSaving,
            isUpdatingCouponName,
            isAddingPromotionCode,
            deletingPromotionCodeId,
            deletingCoupon,
            showPromotionCodeForm,
            form,
            promotionCodeForm,
            handleSaveCoupon,
            handleUpdateCouponName,
            handleAddPromotionCode,
            handleDeletePromotionCode,
            handleDeleteCoupon,
            buildStripeCouponUrl,
            formatDiscount,
            formatDuration,
            formatValidity,
            adminBadgeClass
        };
    }
};
</script>
